import { useState, useEffect } from "react";
import { ILeanAsset, AssetType } from "@nendaTypes/NendaTypes";
import {
  extractMediaInfoFromUrl,
  getVideoDurationFromUrl,
} from "@client/utils/fileUtil";

interface FileInfo {
  fileSize: number | null;
  duration: number | null;
  fileType: string | null;
  mediaType: AssetType.VIDEO | AssetType.IMAGE | null;
  dimensions: {
    width: number | null;
    height: number | null;
  };
}

interface VideoDurationResult {
  duration: number | null;
  error: string | null;
}

export function useVideoDuration(
  asset: ILeanAsset | undefined
): VideoDurationResult {
  const [result, setResult] = useState<VideoDurationResult>({
    duration: null,
    error: null,
  });

  useEffect(() => {
    if (!asset || asset.type !== AssetType.VIDEO) return;

    getVideoDurationFromUrl(asset.locator)
      .then((duration) => {
        setResult({ duration, error: null });
      })
      .catch((error: Error) => {
        setResult({ duration: null, error: error.message });
      });
  }, [asset]);

  return result;
}

const useMediaFileInformation = (asset: ILeanAsset) => {
  const [fileInfo, setFileInfo] = useState<FileInfo>({
    fileSize: null,
    duration: null,
    fileType: null,
    mediaType: null,
    dimensions: {
      width: null,
      height: null,
    },
  });
  const [isLoading, setIsLoading] = useState(true);

  function isImageOrVideoAsset(
    asset: ILeanAsset
  ): asset is ILeanAsset & { type: AssetType.IMAGE | AssetType.VIDEO } {
    return asset.type === AssetType.IMAGE || asset.type === AssetType.VIDEO;
  }

  useEffect(() => {
    if (!asset || !asset.locator || !asset.type) {
      setIsLoading(false);
      return;
    }

    if (isImageOrVideoAsset(asset) === false) {
      setIsLoading(false);
      return;
    }

    const mediaType = asset.type;

    const fetchMediaInfo = async () => {
      try {
        const info = await extractMediaInfoFromUrl(asset.locator, mediaType);

        setFileInfo({
          fileSize: info.fileSize,
          duration: info.duration,
          fileType: info.fileType,
          mediaType: info.mediaType,
          dimensions: {
            width: asset.dimensions?.width ?? info.dimensions.width,
            height: asset.dimensions?.height ?? info.dimensions.height,
          },
        });
      } catch (err) {
        console.warn("Failed to extract media info", err);
        // Fallback to dimensions from asset if available
        setFileInfo({
          fileSize: null,
          duration: null,
          fileType: null,
          mediaType,
          dimensions: {
            width: asset.dimensions?.width ?? null,
            height: asset.dimensions?.height ?? null,
          },
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchMediaInfo();
  }, [asset]);

  return { ...fileInfo, isLoading };
};

export default useMediaFileInformation;
